/**
 * What the browser tab says.
 *
 * A single-page application changes the screen without changing the document, so unless something
 * sets it every page carries the title `index.html` shipped with. That is one line in a tab, but it
 * is also every entry in the history menu, every bookmark and every window in a task switcher —
 * eight identical rows saying "RentorNG", none of which say which one is the invoice.
 *
 * The product name goes **last**. A tab is narrow and truncates from the right, so leading with the
 * brand means twelve tabs all reading "RentorNG — …" and nothing else. What the page *is* comes
 * first, where it survives.
 */

import { useEffect } from "react";

/** What every title ends with, and what the tab falls back to between screens. */
export const PRODUCT = "RentorNG";

/**
 * Set the tab's title for as long as this screen is on it.
 *
 * Pass what the page is, not a sentence: "Invoices", "Flat 3B · Palm Court", "Add a property". It
 * is joined to the product name here so the separator is the same everywhere.
 *
 * Nothing is restored on unmount. The next screen sets its own, and a title that flickers back to
 * the product name in between is worse than one that changes once.
 */
export function usePageTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · ${PRODUCT}` : PRODUCT;
  }, [title]);
}

/**
 * The same, for a title that is being loaded.
 *
 * A detail screen does not know its own name until the fetch lands. Passing the fallback keeps the
 * tab from saying "undefined" for a second, which is the sort of thing that only ever gets noticed
 * in a screenshot.
 */
export const titleOr = (title: string | null | undefined, fallback: string) => title || fallback;
